import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined, LeftOutlined, RightOutlined, ExpandOutlined } from '@ant-design/icons';
import styles from './index.module.scss';

export const MiddlePanel = () => {
  const { t } = useTranslation();
  const previewImage = useSelector((state) => state.global.previewImage);
  const previewFile = useSelector((state) => state.global.previewFile);
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages] = useState(1); // For future multi-page support
  const imageSrc = previewFile?.type === 'image' ? previewFile?.url : previewImage;
  const pdfSrc = previewFile?.type === 'pdf' ? previewFile?.url : null;
  const isPdfPreview = Boolean(pdfSrc);
  const canZoomImage = Boolean(imageSrc) && !isPdfPreview;

  useEffect(() => {
    setZoom(100);
    setCurrentPage(1);
  }, [imageSrc, pdfSrc]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };

  const handleFitToWidth = () => {
    setZoom(100);
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  return (
    <div className={styles.middlePanel}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <span className={styles.pageInfo}>
            {t('middlePanel.pageInfo', { currentPage, totalPages })}
          </span>
          <div className={styles.buttonGroup}>
            <Tooltip title={t('middlePanel.previousPage')}>
              <Button 
                icon={<LeftOutlined />} 
                size="small"
                disabled={isPdfPreview || currentPage === 1}
                onClick={handlePrevPage}
              />
            </Tooltip>
            <Tooltip title={t('middlePanel.nextPage')}>
              <Button 
                icon={<RightOutlined />} 
                size="small"
                disabled={isPdfPreview || currentPage === totalPages}
                onClick={handleNextPage}
              />
            </Tooltip>
          </div>
        </div>

        <div className={styles.toolbarRight}>
          <div className={styles.buttonGroup}>
            <Tooltip title={t('middlePanel.zoomOut')}>
              <Button 
                icon={<ZoomOutOutlined />} 
                size="small"
                onClick={handleZoomOut}
                disabled={!canZoomImage || zoom <= 50}
              />
            </Tooltip>
            <span className={styles.zoomLevel}>{zoom}%</span>
            <Tooltip title={t('middlePanel.zoomIn')}>
              <Button 
                icon={<ZoomInOutlined />} 
                size="small"
                onClick={handleZoomIn}
                disabled={!canZoomImage || zoom >= 200}
              />
            </Tooltip>
            <Tooltip title={t('middlePanel.fitToWidth')}>
              <Button 
                icon={<ExpandOutlined />} 
                size="small"
                onClick={handleFitToWidth}
                disabled={!canZoomImage}
              />
            </Tooltip>
          </div>
        </div>
      </div>

      <div className={styles.previewContainer}>
        {pdfSrc ? (
          <div className={styles.pdfPreviewWrap}>
            <iframe
              title={previewFile?.name || t('middlePanel.previewAlt')}
              src={pdfSrc}
              className={styles.pdfPreview}
              loading="lazy"
            />
          </div>
        ) : imageSrc ? (
          <div className={styles.previewContent}>
            <img
              src={imageSrc}
              alt={t('middlePanel.previewAlt')}
              className={styles.previewImage}
              style={{ width: `${zoom}%` }}
            />
          </div>
        ) : previewFile?.type === 'pdf' ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyBadge}>{t('middlePanel.previewBadge')}</div>
            <p className={styles.emptyText}>{t('middlePanel.pdfPreviewUnavailable')}</p>
            <p className={styles.emptyHint}>{previewFile?.name || t('middlePanel.supportedFormats')}</p>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyBadge}>{t('middlePanel.previewBadge')}</div>
            <p className={styles.emptyText}>{t('middlePanel.previewPlaceholder')}</p>
            <p className={styles.emptyHint}>{t('middlePanel.previewHint')}</p>
          </div>
        )}
      </div>
    </div>
  );
};
